import { el, clear } from "../utils/dom.ts";
import { getInsights, type Insight } from "../services/api-client.ts";
import { renderBriefMarkdown } from "../utils/sanitize-markdown.ts";
import { signalSourceLabel } from "./signal-source-label.ts";

// Alert feed for the Tổng quan tab — the single anomaly surface plus the national
// weekly brief and its domain outlook. Every LLM-authored scalar (titles) is set
// via text nodes (el() text-nodes strings); the brief body goes through
// renderBriefMarkdown (pinned DOMPurify profile). No raw innerHTML of untrusted text.
export class AlertFeedPanel {
  readonly el: HTMLElement;
  private body: HTMLElement;

  constructor() {
    this.body = el("div", { className: "alert-feed-body" }, ["Đang tải…"]);
    this.el = el("section", { className: "panel alert-feed-panel" }, [
      el("div", { className: "panel-head" }, [
        el("h2", {}, ["⚠ Cảnh báo & Tổng hợp tuần"]),
        signalSourceLabel("media"),
      ]),
      this.body,
    ]);
  }

  async load(): Promise<void> {
    try {
      const [anomaliesRes, briefRes, outlookRes] = await Promise.all([
        getInsights("?type=anomaly&limit=10"),
        getInsights("?scope=national&type=weekly-brief&limit=1"),
        getInsights("?scope=national&type=outlook&limit=8"),
      ]);
      this.render(anomaliesRes.items, briefRes.items[0], outlookRes.items);
    } catch {
      clear(this.body);
      this.body.append(el("p", { className: "muted" }, ["Không tải được cảnh báo."]));
    }
  }

  private render(anomalies: Insight[], brief: Insight | undefined, outlook: Insight[]): void {
    clear(this.body);

    // Anomaly cards (or an accumulating-baseline empty state).
    if (anomalies.length === 0) {
      this.body.append(
        el("p", { className: "muted alert-empty" }, [
          "Chưa phát hiện bất thường — hệ thống đang tích lũy dữ liệu nền để so sánh.",
        ]),
      );
    } else {
      const list = el("div", { className: "alert-list" });
      for (const a of anomalies) {
        list.append(
          el("div", { className: "alert-card" }, [
            el("div", { className: "alert-title" }, [a.title]), // text node — safe
            a.confidence != null
              ? el("span", { className: "alert-conf" }, [
                  `Độ tin cậy ${Math.round(a.confidence * 100)}%`,
                ])
              : "",
          ]),
        );
      }
      this.body.append(list);
    }

    // National weekly brief. Distinct class from AnalysisBriefPanel's .brief-body
    // so tab-level selectors stay unambiguous.
    if (brief) {
      const briefBody = el("div", { className: "alert-brief-body" });
      briefBody.innerHTML = renderBriefMarkdown(brief.body_vi); // sanitized
      this.body.append(
        el("div", { className: "alert-brief" }, [
          el("h3", {}, [brief.title]), // text node
          briefBody,
          el("div", { className: "alert-week" }, [`Tuần ${brief.week}`]),
        ]),
      );
    }

    // Domain outlook (structured rows from Phase 7). Rendered from structure.
    if (outlook.length > 0) {
      const items = el("ul", { className: "outlook-list" });
      for (const o of outlook) {
        items.append(el("li", {}, [o.title, o.body_vi ? ` — ${o.body_vi}` : ""]));
      }
      this.body.append(
        el("div", { className: "alert-outlook" }, [
          el("h3", {}, ["Triển vọng tuần tới"]),
          items,
          el("p", { className: "outlook-disclaimer" }, [
            "Tham khảo — nhận định định tính từ tín hiệu truyền thông, không phải khuyến nghị.",
          ]),
        ]),
      );
    }
  }
}
